import { useQuery } from "@tanstack/react-query";
import type { FormDefinition, FormStatus } from "@colab/shared";

// Public embed read hook.
//
// Iframed from third-party origins — the SPA's `apiFetch` wrapper attaches
// the Supabase session cookie + Bearer header via `credentials: 'include'`,
// which a cross-origin iframe will silently fail or rewrite. Use raw `fetch`
// with `credentials: 'omit'` so we explicitly don't try to carry session
// state from the parent page.
//
// The endpoint (`GET /api/forms/:slug`) is anonymous and returns only
// published forms.

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

export type EmbedFormRow = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  status: FormStatus;
  definition: FormDefinition;
  definition_version: number;
};

export class EmbedFormNotFoundError extends Error {
  constructor() {
    super("form_not_found");
    this.name = "EmbedFormNotFoundError";
  }
}

export function useEmbedForm(slug: string) {
  return useQuery<EmbedFormRow, Error>({
    queryKey: ["embed", "form", slug],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/forms/${slug}`, {
        method: "GET",
        credentials: "omit",
        headers: { accept: "application/json" },
      });
      if (res.status === 404) throw new EmbedFormNotFoundError();
      if (!res.ok) {
        throw new Error(`Failed to load form (HTTP ${res.status})`);
      }
      const body = (await res.json()) as { form: EmbedFormRow };
      return body.form;
    },
    // Embeds are mounted once per iframe — no need to refetch on focus / mount
    // (the host page can't drive Tanstack focus events anyway).
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Submit. Mirrors the legacy `EmbedFormRenderer` translation layer: API errors
// map to { ok: false, error, issues? }; success maps to { ok: true, id }.
// ---------------------------------------------------------------------------

export type EmbedSubmitResult =
  | {
      ok: true;
      id: string;
      autoAccepted: boolean;
      downstream: { kind: string; id: string } | null;
    }
  | {
      ok: false;
      error: string;
      issues?: ReadonlyArray<{ path: ReadonlyArray<PropertyKey>; message: string }>;
      retryAfterMs?: number;
    };

export async function submitEmbedForm(
  slug: string,
  payload: Record<string, unknown>,
  options?: {
    submitterEmail?: string;
    submitterName?: string;
    turnstileToken?: string;
  },
): Promise<EmbedSubmitResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/forms/${slug}/submit`, {
      method: "POST",
      credentials: "omit",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        payload,
        submitterEmail: options?.submitterEmail,
        submitterName: options?.submitterName,
        turnstileToken: options?.turnstileToken,
      }),
    });
  } catch (err) {
    return {
      ok: false,
      error:
        err instanceof Error ? err.message : "Network error — please retry.",
    };
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return {
      ok: false,
      error: `Unexpected response from server (HTTP ${res.status})`,
    };
  }

  if (!res.ok || (body && typeof body === "object" && "error" in body)) {
    const obj = body as {
      error?: string;
      issues?: ReadonlyArray<{
        path?: ReadonlyArray<PropertyKey>;
        message?: string;
      }>;
      retryAfterMs?: number;
    };
    const rawError = obj.error ?? `Submit failed (HTTP ${res.status})`;
    let message = rawError;
    if (rawError === "rate_limited") {
      message =
        "Too many submissions in a short window. Please wait a minute and try again.";
    } else if (rawError === "validation") {
      message = "Please correct the highlighted fields.";
    } else if (rawError === "form_not_found") {
      message = "This form is no longer available.";
    } else if (rawError === "turnstile_failed") {
      message =
        "We couldn't verify you're human. Please complete the challenge and try again.";
    }
    return {
      ok: false,
      error: message,
      issues: obj.issues?.map((i) => ({
        path: i.path ?? [],
        message: i.message ?? "Invalid value",
      })),
      retryAfterMs: obj.retryAfterMs,
    };
  }

  const okBody = body as {
    id: string;
    autoAccepted?: boolean;
    downstream?: { kind: string; id: string } | null;
  };
  return {
    ok: true,
    id: okBody.id,
    autoAccepted: okBody.autoAccepted ?? false,
    downstream: okBody.downstream ?? null,
  };
}
